import { memo } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCopyContent } from '@components/icons/copy-content';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export interface CodeBlockProps {
  header?: string;
  data: object;
  hideFooter?: boolean;
  maxHeight?: string;
}

function syntaxHighlight(json: string) {
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    function (match) {
      var cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }
      return '<span class="' + cls + '">' + match + '</span>';
    }
  );
}

export const CodeBlock = memo(function CodeBlock({
  header,
  data,
  hideFooter,
  maxHeight = pxToRem(384),
}: CodeBlockProps) {
  const { copied, onCopy } = useClipboard();
  const stringifiedData = JSON.stringify(data, null, 2);
  const highlightedJSON = syntaxHighlight(stringifiedData);
  const codeLines = highlightedJSON.split('\n').map((line, index) => (
    <div key={index}>
      <span className="line-number">{index + 1}</span>
      <span dangerouslySetInnerHTML={{ __html: line }}></span>
    </div>
  ));
  const hideHeader = !header;

  return (
    <CodeContainer>
      {!hideHeader && (
        <HeaderFooterContainer>
          <Text font="bold14" text={header} color="textSecondary" />
        </HeaderFooterContainer>
      )}
      <StyledCodeBlock $hideHeader={hideHeader} $hideFooter={hideFooter} $maxHeight={maxHeight}>
        {codeLines}
      </StyledCodeBlock>
      {!hideFooter && (
        <HeaderFooterContainer>
          <IconButton
            variant="ghost-purple"
            icon={copied ? <IconCheckmark /> : <IconCopyContent />}
            onClick={() => onCopy(stringifiedData)}
          />
        </HeaderFooterContainer>
      )}
    </CodeContainer>
  );
});

const CodeContainer = styled.div({
  border: `1px solid ${theme.color.border20}`,
  borderRadius: theme.space[2],
  backgroundColor: theme.color.uiTrans5,
});

const HeaderFooterContainer = styled.div({
  padding: `${theme.space[2]} ${theme.space[3]}`,
});

const StyledCodeBlock = styled.pre<{
  $hideHeader?: boolean;
  $hideFooter?: boolean;
  $maxHeight?: string;
}>(({ $hideHeader = false, $hideFooter = false, $maxHeight = pxToRem(384) }) => ({
  fontSize: pxToRem(14),
  overflow: 'auto',
  maxHeight: $maxHeight,
  margin: 0,
  flexGrow: 1,
  ...(!$hideHeader && {
    borderTop: `1px solid ${theme.color.border20}`,
  }),
  ...(!$hideFooter && {
    borderBottom: `1px solid ${theme.color.border20}`,
  }),
  padding: `${theme.space[2]} ${theme.space[3]}`,
  'word-wrap': 'break-word',
  'white-space': 'pre',
  color: theme.color.textPrimary,
  '.line-number': {
    color: theme.color.elementPurple50,
    display: 'inline-block',
    width: theme.space[10],
    textAlign: 'right',
    paddingRight: theme.space[1],
    opacity: 0.5,
  },
  '.string': {
    color: theme.color.elementGreen100,
  },
  '.boolean': {
    color: theme.color.elementIndigo100,
  },
  '.number': {
    color: theme.color.elementOrange100,
  },
  '.null': {
    color: theme.color.elementPurple80,
  },
  '.key': {
    color: theme.color.elementBlue100,
  },
}));
